import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import t from './tokens';

// Horizontal photo carousel with scroll-indicator dots, mirroring the Figma
// gallery's per-student photo strip. Dots paginate by viewport-width (one dot
// per "page") rather than one-per-photo, so the indicator stays readable even
// with many poses. The cards themselves are passed in as children.
const dot = {
  border: 'none',
  padding: 0,
  height: 8,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'width .3s, background .3s',
};

const PhotoRail = ({ children }) => {
  const ref = useRef(null);
  const [pages, setPages] = useState(1);
  const [active, setActive] = useState(0);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const perPage = el.clientWidth || 1;
    setPages(Math.max(1, Math.ceil(el.scrollWidth / perPage)));
    setActive(Math.round(el.scrollLeft / perPage));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure, children]);

  const goto = (i) => {
    const el = ref.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="sg-rail" ref={ref}>{children}</div>
      {pages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 6, marginTop: '0.75rem',
        }}
        >
          {Array.from({ length: pages }, (_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to photo group ${i + 1}`}
              onClick={() => goto(i)}
              style={{
                ...dot,
                width: active === i ? 28 : 8,
                background: active === i ? t.brand : '#d1d5db',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

PhotoRail.propTypes = { children: PropTypes.node.isRequired };

export default PhotoRail;
